import React, {
  useCallback,
  useRef,
  useState,
} from "react";

export const SIDE_LENGTH = 20; // Size of each day cell in pixels

const SessionTimeline = ({
                           data,
                           onDateSelection,
                           selectedDate,
                           earliestDate,
                           startDate,
                           endDate,
                         }) => {
  const jump_days = 30; // Approximately a month.
  const outerContainerRef = useRef(null);
  const [tooltip, setTooltip] = useState({
    visible: false,
    content: "",
    x: 0,
    y: 0,
  });

  /**
   * Handle the click on a day cell.
   * @param dataEntry - the date information send back up the stack to select a new date.
   */
  const handleDayClick = (dataEntry) => {
    onDateSelection(dataEntry.date, dataEntry.total);
  };

  /**
   * Show the tooltip when the mouse hovers over a day cell.
   * @param event - the event object available for all such events, used to find the cell.
   * @param d - provides the important date information to show in the tooltip.
   */
  const handleMouseover = (event, d) => {
    // Fix tooltip positioning by getting coordinates relative to the container
    const rect = event.currentTarget.getBoundingClientRect();
    const containerRect = outerContainerRef.current.getBoundingClientRect();

    const newTooltip = {
      visible: true,
      x: rect.left - containerRect.left + SIDE_LENGTH / 2,
      y: rect.bottom - containerRect.top,
      ...d
    };
    setTooltip(newTooltip)
  };

  /**
   * Close the tooltip when the mouse exits the day cells.
   */
  const handleMouseout = () => {
    setTooltip({...tooltip, visible: false});
  }

  /**
   * Determine if the button pointing to previous days should be shown.
   * @returns {boolean}
   */
  const previousDaysAvailable = useCallback(() => {
    // If the values aren't available, just assume earlier days are available.
    if (!startDate || !earliestDate) return true;

    // If somehow we have a start date earlier than the earliest date (likely an off-by-one type error),
    // we definitely don't have previous days available.
    if (earliestDate >= startDate) return false;

    // Other than that, just check if they have the same date string (technically different
    // from above because that compares the absolute date and time)
    return earliestDate.toDateString() !== startDate.toDateString();
  }, [earliestDate, startDate]);

  /**
   * Determine if the button pointing to future days is available.
   * @returns {boolean}
   */
  const futureDaysAvailable = useCallback(() => {
    // If there isn't an available end date, assume there are no future days
    // available.
    if (!endDate) return false;

    // the latest possible date is today, of course.
    const latestDate = new Date();

    // The logic is the same as in previousDaysAvailable from here.
    if (latestDate <= endDate) return false;
    return latestDate.toDateString() !== endDate.toDateString();
  }, [endDate]);

  /**
   * Jump half the data range of the date bar in response to a button click.
   * @param {int} sign - +1 to jump forward or -1 to jump back
   */
  const handleJumpRange = (sign) => {
    const jumpMs = jump_days * 24 * 60 * 60 * 1000;

    // "But...past developer!" you say, "a double negative makes no sense!"
    // Ah, you would think so! But JavaScript is a special languate, and Date + number => string, while
    // Date - number => number (interpretable as a new date).
    const newDate = new Date(selectedDate - (-Math.sign(sign) * jumpMs));
    onDateSelection(newDate)
  }

  /**
   * Determine whether to show the year for a given date cell.
   * We show the year if it's Jan 1st or the first cell in the timeline.
   * @param {Date} dateObj - The date for the current cell.
   * @param {int} idx - the index of the current cell
   * @returns {boolean}
   */
  const showYear = (dateObj, idx) => {
    return (dateObj.getMonth() === 0 && dateObj.getDate() === 1) || idx === 0
  }

  /**
   * Determine whether to show the month for a given date cell.
   * We show the month on the first of the month or at the start of the list for context.
   * @param {Date} dateObj - The date of the current cell
   * @param {int} idx - The index of the current cell
   * @returns {boolean}
   */
  const showMonth = (dateObj, idx) => {
    return (dateObj.getDate() === 1 || idx === 0)
  }

  /**
   * Select the background color based on cell properties.
   * @param dataEntry
   * @returns {string}
   */
  const getCellBackgroundColor = (dataEntry) => {
    if (dataEntry.date.toDateString() === selectedDate.toDateString())
      return "rgba(225, 0, 225, .75)"
    if (dataEntry.total > 0)
      return "rgba(66, 153, 225, 0.6)"
    return "rgba(66, 153, 225, 0.2)"
  }

  return (
    <div className="z-1">
      <div
        style={{
          background: "transparent",
          maxWidth: "94%",
          margin: "0 auto",
        }}
      >
        <div className="day-group flex items-center justify-center m-5" ref={outerContainerRef} style={{
          fontSize: 14,
          color: "white",
          height: SIDE_LENGTH,
        }}>
          {previousDaysAvailable() &&
            <button
              className="flex relative"
              onClick={() => handleJumpRange(-1)}
              title={`Jump back ${jump_days} days.`}>
              ◀
            </button>
          }
          {data.map((d, idx) => (
            <div className="day-cell flex relative">
              {showYear(d.date, idx) &&
                <div style={{
                  fontWeight: "bold",
                  marginLeft: 6,
                  marginRight: 2,
                }}>
                  {d.date.getFullYear()}
                </div>
              }
              {showMonth(d.date, idx) &&
                <div style={{
                  marginLeft: 4,
                  marginRight: 2
                }}>
                  {d.date.toDateString().split(' ')[1]}
                </div>
              }
              <div
                onClick={() => handleDayClick(d)}
                onMouseOver={(event) => handleMouseover(event, d)}
                onMouseOut={handleMouseout}
                key={d.date.toDateString()}
                style={{
                  width: `${SIDE_LENGTH}px`,
                  backgroundColor: getCellBackgroundColor(d),
                  borderRadius: "5px",
                  textAnchor: "middle",
                  textAlign: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  marginRight: 1,
                  marginLeft: 1,
                }}>
                {d.date.getDate()}
              </div>
            </div>
          ))}
          {futureDaysAvailable() &&
            <button
              className="flex relative"
              onClick={() => handleJumpRange(+1)}
              title={`Jump back ${jump_days} days.`}
            >
              ▶
            </button>
          }
        </div>
        {tooltip.visible && (
          <div
            className="fixed bg-gray-800 bg-opacity-30 backdrop-blur-lg text-gray-300 p-2 rounded shadow-lg z-50 text-xs text-right transition-opacity duration-300"
            style={{
              left: `${tooltip.x}px`,
              top: `${tooltip.y + 80}px`,
              pointerEvents: "none",
              width: "100px",
            }}
          >
            {tooltip.date.toDateString()}<br/>
            {tooltip.voice} calls<br/>
            {tooltip.text} texts<br/>
            <b>{tooltip.total} sessions</b><br/>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(SessionTimeline);
